import { useState } from "react";
import { HTTP_STATUSES } from "@miplatform/constants";
import {
  IApiToastMessageConfig,
  ICategoriesResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  useCategories,
  useCustomerMaterials,
  useMaterials,
} from "@mibuildweb/hooks";
import { groupAndSortMaterialsByCategory } from "../../CustomerPricingDetails/useCustomerPricingDetailsViewModel";

export type CategoryTab = { id: string; name: string };

export const useCustomerItemsAssignTabViewModel = () => {
  const [groupedMaterials, setGroupedMaterials] = useState<
    Record<string, IMaterialsResponse[]>
  >({});
  const [groupedCustomerMaterials, setGroupedCustomerMaterials] = useState<
    Record<string, IMaterialsResponse[]>
  >({});
  const [categoryTabs, setCategoryTabs] = useState<CategoryTab[]>([]);
  const [selectedTab, setSelectedTab] = useState<CategoryTab | null>(null);

  const handleGetMaterials = async () => {
    const { status, data }: { status: number; data: IMaterialsResponse[] } =
      await getMaterials();

    if (status === HTTP_STATUSES.OK) {
      setMaterials(data);
      setGroupedMaterials(groupAndSortMaterialsByCategory(data));
    }
  };

  const handleGetCustomerMaterials = async (accountId: string) => {
    const { status, data }: { status: number; data: IMaterialsResponse[] } =
      await getCustomerMaterials({ accountId });

    if (status === HTTP_STATUSES.OK) {
      setCustomerMaterials(data);
      setGroupedCustomerMaterials(groupAndSortMaterialsByCategory(data));
    }
  };

  const handleUpdateCustomerMaterials = async (
    accountId: string,
    materialIds: string[],
    toastMessageConfig?: IApiToastMessageConfig
  ) => {
    const materialsById = new Map(customerMaterials.map((m) => [m.id, m]));
    const materials = materialIds.map((id) => ({
      id,
      netSalesPrice: materialsById.get(id)?.netSalesPrice ?? null,
    }));
    const response = await updateCustomerMaterials(
      accountId,
      materials,
      toastMessageConfig
    );

    if (response.status === HTTP_STATUSES.OK) {
      setCustomerMaterials(response.data);
      setGroupedCustomerMaterials(
        groupAndSortMaterialsByCategory(response.data)
      );
    }

    return response;
  };

  const handleGetCategories = async () => {
    const response = await getCategories();
    if (response.status === HTTP_STATUSES.OK) {
      const tabs = response.data.map((c: ICategoriesResponse) => ({
        id: c.id,
        name: c.name,
      }));
      if (tabs.length > 0) {
        setCategoryTabs(tabs);
        setSelectedTab(tabs[0]);
      }
    }
  };

  const handleTabChange = (tab: CategoryTab) => {
    setSelectedTab(tab);
  };

  const {
    materials,
    getMaterials,
    setMaterials,
    isLoading: isMaterialsLoading,
  } = useMaterials();

  const {
    categories,
    getCategories,
    isLoading: isCategoriesLoading,
  } = useCategories();

  const {
    customerMaterials,
    getCustomerMaterials,
    setCustomerMaterials,
    updateCustomerMaterials,
    isLoading: isCustomerMaterialsLoading,
  } = useCustomerMaterials();

  return {
    materials,
    groupedMaterials,
    groupedCustomerMaterials,
    categories,
    categoryTabs,
    selectedTab,
    handleGetMaterials,
    handleGetCustomerMaterials,
    handleUpdateCustomerMaterials,
    handleGetCategories,
    handleTabChange,
    customerMaterials,
    isLoading:
      isMaterialsLoading || isCategoriesLoading || isCustomerMaterialsLoading,
  };
};
